import * as THREE from 'three';
import { content, renderer, scene } from '../scene/context.js';
import { syncNode } from '../scene/objects.js';
import { requestRender } from '../state/bus.js';
import { S } from '../state/store.js';

/* lights */
const amb = new THREE.AmbientLight(0xffffff, .5);
const hemi = new THREE.HemisphereLight(0xbfd4e6, 0x2a2622, .55);
export const key = new THREE.DirectionalLight(0xffffff, 2.1);
key.castShadow = true;
key.shadow.mapSize.set(2048,2048);
key.shadow.radius = 8;      // PCFShadowMap's blur radius, in texels — reconciled to S.shadowSoft by setShadowSoft() at boot
key.shadow.bias = -0.0006;
key.shadow.normalBias = 0.02;
const rim = new THREE.DirectionalLight(0xa8c8ff, .7);
scene.add(amb, hemi, key, rim, key.target);

/* Direct lights alone make MeshStandardMaterial look like plastic — the specular
   has nothing to reflect. Each preset therefore also builds a small emissive
   scene and pre-filters it into an environment map with PMREMGenerator. */
/* 'dark' is deliberately absent: it takes no environment map at all rather than a
   black one, so scene.environment stays null and nothing gets pre-filtered. */
const ENVSPEC = {
  studio:{ grad:[[0,'#e9eef4'],[.48,'#8b96a1'],[.52,'#3b4249'],[1,'#181c20']],
           lamps:[[-5,4,6,7,7,'#ffffff',3.2],[6,1,4,5,6,'#dfe8f5',1.1],[0,2,-7,8,6,'#c8d4e2',.55]] },
  sun:   { grad:[[0,'#8fc4f5'],[.46,'#dceafb'],[.54,'#8a7f6e'],[1,'#3b342b']],
           lamps:[[6,7,5,4,4,'#fff3d8',9],[-6,2,3,8,8,'#bcd8ff',1.3],[0,-3,4,9,5,'#c9b593',.7]] },
  night: { grad:[[0,'#0d1420'],[.5,'#131b26'],[.52,'#0a0d11'],[1,'#05070a']],
           lamps:[[-6,3,5,4,5,'#cfe0ff',3.4],[7,1,-3,3,4,'#ff9d5c',1.6],[0,6,-6,6,3,'#7fa8ff',.7]] },
  flat:  { grad:[[0,'#c9ced4'],[1,'#c9ced4']],
           lamps:[[0,0,8,14,14,'#ffffff',1.2]] }
};

let envRT = null;
export function buildEnvironment(){
  if(S.lighting === 'dark'){
    if(envRT){ envRT.dispose(); envRT = null; }
    scene.environment = null;
    return;
  }
  const L = ENVSPEC[S.lighting] || ENVSPEC.studio;
  const es = new THREE.Scene();
  const disposables = [];

  const cv = document.createElement('canvas'); cv.width = 4; cv.height = 512;
  const c2 = cv.getContext('2d');
  const g = c2.createLinearGradient(0,0,0,512);
  L.grad.forEach(([t,c])=>g.addColorStop(t,c));
  c2.fillStyle = g; c2.fillRect(0,0,4,512);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.mapping = THREE.EquirectangularReflectionMapping;
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(12, 40, 28),
    new THREE.MeshBasicMaterial({map:tex, side:THREE.BackSide})
  );
  es.add(dome); disposables.push(dome, tex);

  L.lamps.forEach(([x,y,z,w,h,c,i])=>{
    const m = new THREE.Mesh(
      new THREE.PlaneGeometry(w,h),
      new THREE.MeshBasicMaterial({color:new THREE.Color(c).multiplyScalar(i)})
    );
    m.position.set(x,y,z); m.lookAt(0,0,0);
    es.add(m); disposables.push(m);
  });

  try{
    const pm = new THREE.PMREMGenerator(renderer);
    const rt = pm.fromScene(es, .035, .1, 60);
    pm.dispose();
    if(envRT) envRT.dispose();
    envRT = rt;
    scene.environment = rt.texture;
  }catch(e){
    scene.environment = null;   // fall back to direct lights only
  }
  disposables.forEach(o=>{ if(o.geometry) o.geometry.dispose(); if(o.material) o.material.dispose(); if(o.dispose && !o.isMesh) o.dispose(); });
}

export function applyEnvIntensity(){
  content.traverse(o=>{
    if(o.material && 'envMapIntensity' in o.material){
      o.material.envMapIntensity = S.envInt;
      o.material.needsUpdate = true;
    }
  });
}

export function setShadowRes(n){
  const changed = S.shadowRes !== n;
  S.shadowRes = n;
  if(changed) S.objects.filter(ob=>ob.type==='light').forEach(syncNode);
  if(key.shadow.map){ key.shadow.map.dispose(); key.shadow.map = null; }
  key.shadow.mapSize.set(n,n);
  requestRender();
}

/* Blur radius in shadow-map texels, read by VSMShadowMap (the renderer's
   configured mode — see scene/context.js; PCFSoftShadowMap's softness is
   fixed and ignores this entirely) to soften the shadow edge. Unlike
   resolution this needs no map rebuild, so every light's shadow.radius can
   just be written directly. One knob for every light in the scene — the
   preset's key light and any Light objects the user added — so "softer
   shadows" means the same thing regardless of which lighting scenario or
   how many lamps are in play. */
export function setShadowSoft(n){
  S.shadowSoft = n;
  key.shadow.radius = n;
  S.objects.forEach(ob=>{
    if(ob.type!=='light' || !ob.node) return;
    const L = ob.node.userData.light;
    if(L && L.shadow) L.shadow.radius = n;
  });
  requestRender();
}

/* Bounds of the actual subject. Light bodies are excluded, or a lamp parked off to
   one side would inflate the bounding sphere and waste the shadow map on nothing. */
function contentBounds(){
  const box = new THREE.Box3();
  S.objects.forEach(ob=>{
    if(ob.type==='light' || ob.type==='camera' || ob.type==='null' || !ob.node || !ob.visible) return;
    box.expandByObject(ob.node);
  });
  return box;
}

/* A shadow camera sized to the whole set wastes almost all of its resolution, so
   every shadow-casting light gets fitted to the subject's bounding sphere. */
export function fitAllShadows(){
  const box = contentBounds();
  const c = new THREE.Vector3(), sz = new THREE.Vector3();
  let r;
  if(box.isEmpty()){
    c.set(0,0,0); r = sceneSpan()*.75;
  }else{
    box.getCenter(c); box.getSize(sz);
    r = Math.max(.5, sz.length()*.5*1.45);   // margin for shadows thrown onto the ground
  }

  const P = LIGHTS[S.lighting] || LIGHTS.studio;
  const { az, el } = effectiveLightAzEl();
  const dir = dirFromAzEl(az, el);
  key.castShadow = P.ki > 0;                 // no point rendering a map for a dead light
  key.target.position.copy(c);
  key.target.updateMatrixWorld();
  key.position.copy(c).addScaledVector(dir, r*3);
  const sc = key.shadow.camera;
  sc.left=-r; sc.right=r; sc.top=r; sc.bottom=-r;
  sc.near = r*1.2; sc.far = r*5.2;
  sc.updateProjectionMatrix();

  const wp = new THREE.Vector3();
  S.objects.forEach(ob=>{
    if(ob.type!=='light' || !ob.node) return;
    const L = ob.node.userData.light;
    if(!L || !L.castShadow || !L.shadow) return;
    L.getWorldPosition(wp);
    const d = Math.max(.2, wp.distanceTo(c));
    const cam = L.shadow.camera;
    cam.near = Math.max(.05, d - r*1.3);
    cam.far  = d + r*1.6;
    if(L.isDirectionalLight){ cam.left=-r; cam.right=r; cam.top=r; cam.bottom=-r; }
    cam.updateProjectionMatrix();
  });
}

const LIGHTS = {
  dark:  {dir:[.55,.68,.49], ki:0,  kc:0xffffff, rim:[-.72,.3,-.62], ri:0,   amb:0,   hemi:0},
  studio:{dir:[.55,.68,.49], ki:2.0, kc:0xffffff, rim:[-.72,.3,-.62], ri:.55, amb:.16, hemi:.20},
  sun:   {dir:[.62,.70,.35], ki:3.6, kc:0xfff1d6, rim:[-.66,.22,-.72], ri:.28, amb:.08, hemi:.12},
  night: {dir:[-.48,.66,.58], ki:1.2, kc:0xd2e2ff, rim:[.74,.16,-.65], ri:.85, amb:.05, hemi:.10},
  flat:  {dir:[.1,.25,.96], ki:.55, kc:0xffffff, rim:[0,0,1], ri:0, amb:.55, hemi:.25}
};

/* The key light's direction as azimuth (compass heading around Y, degrees)
   and elevation (degrees above the horizon), rather than a raw xyz vector —
   two sliders read and write naturally, a vector doesn't. S.lightAz/lightEl
   are overrides (null = "use this preset's own built-in direction"), so
   switching presets shows that preset's natural default until nudged, and
   "reset" is just setting both back to null. */
function dirFromAzEl(az, el){
  const a = az*Math.PI/180, e = el*Math.PI/180;
  return new THREE.Vector3(Math.cos(e)*Math.sin(a), Math.sin(e), Math.cos(e)*Math.cos(a));
}
function azElFromDir(dir){
  const d = dir.clone().normalize();
  return { az: Math.atan2(d.x, d.z)*180/Math.PI, el: Math.asin(Math.max(-1,Math.min(1,d.y)))*180/Math.PI };
}
export function effectiveLightAzEl(){
  const P = LIGHTS[S.lighting] || LIGHTS.studio;
  const base = azElFromDir(new THREE.Vector3(...P.dir));
  return {
    az: S.lightAz==null ? base.az : S.lightAz,
    el: S.lightEl==null ? base.el : S.lightEl
  };
}

/* How far the piece reaches, for shadow and environment radii. A corner screen
   extends a second panel's width away from the first in depth, so sizing off the
   first panel alone leaves shadows clipping partway along the second. */
function sceneSpan(){
  return Math.max(S.screen.w, S.screen.h, S.cornerPanel ? S.cornerPanel.w : 0);
}

export function applyLighting(rebuildEnv){
  const P = LIGHTS[S.lighting] || LIGHTS.studio;
  const d = sceneSpan();
  const baseDir = new THREE.Vector3(...P.dir).normalize();
  const { az, el } = effectiveLightAzEl();
  const dir = dirFromAzEl(az, el);
  /* Key and rim rotate together as a rig, preserving the preset's own
     designed angle between them — the rim stays a backlight relative to
     the key rather than drifting to a fixed world direction as the key
     is rotated. The environment map (reflections) is intentionally left
     alone: rebuilding it on every drag is the heavier PMREMGenerator path,
     and the reflected "mood" is closer to a backdrop than a literal sun. */
  const q = new THREE.Quaternion().setFromUnitVectors(baseDir, dir);
  const rimDir = new THREE.Vector3(...P.rim).normalize().applyQuaternion(q);
  key.intensity = P.ki; key.color.setHex(P.kc);
  rim.position.set(rimDir.x*d*2, rimDir.y*d*2, rimDir.z*d*2);
  rim.intensity = P.ri;
  amb.intensity = P.amb; hemi.intensity = P.hemi;
  fitAllShadows();
  if(rebuildEnv !== false) buildEnvironment();
  applyEnvIntensity();
}

/* No ground plane. An anamorphic render is what the screen shows, so a studio
   floor would become part of the deliverable. Shadows still fall on the recess
   interior and between objects, which is what actually reads. */

/* Screen frame + frustum.

   These sit exactly on the screen plane, and so does the front face of a recess
   — that is what a recess is. Two coplanar surfaces both depth-testing at the
   same value makes the GPU tie-break per pixel, which shows up as a stippled
   checkerboard along the border. polygonOffset biases the helpers in depth
   without moving them: the reference surface is pushed back so real geometry
   always wins, and the outline is pulled forward so it stays readable. */
